import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OrderFromDB,
  OrderAndSocketState,
  OrderPagination,
  OrderSummary,
} from "./types/OrderAndSocketState";

const initialState: OrderAndSocketState = {
  isSocketActive: false,
  orders: [],
  pagination: {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
  },
  summary: {
    activeCount: 0,
    deliveredCount: 0,
    cancelledCount: 0,
    revenue: 0,
    coinsIssued: 0,
  },
  currentStatuses: null,
};

const parseStatusFilter = (status?: string) => {
  if (!status || status === "all") return null;
  const statusMap: Record<string, number> = {
    pending: 0,
    processing: 1,
    delivered: 2,
    cancelled: 3,
  };

  return status
    .split(",")
    .map((item) => statusMap[item.trim()])
    .filter((item) => item !== undefined);
};

const normalizeOrderStatus = (status: OrderFromDB["status"]) => {
  if (typeof status === "number") return status;
  const statusMap: Record<string, number> = {
    pending: 0,
    processing: 1,
    delivered: 2,
    cancelled: 3,
  };

  return statusMap[String(status).toLowerCase()] ?? 0;
};

export const OrderAndSocket = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSocketConnect: (state, action: PayloadAction<{ isActive: boolean }>) => {
      return {
        ...state,
        isSocketActive: action.payload.isActive,
      };
    },
    setSocketDisconnect: (
      state,
      action: PayloadAction<{ isActive: boolean }>
    ) => {
      return {
        ...state,
        isSocketActive: action.payload.isActive,
      };
    },
    setOrders: (state, action: PayloadAction<OrderFromDB[]>) => {
      state.orders = action.payload;
    },
    appendOrders: (state, action: PayloadAction<OrderFromDB[]>) => {
      const existingIds = new Set(state.orders.map((order) => order.id));
      state.orders = [
        ...state.orders,
        ...action.payload.filter((order) => !existingIds.has(order.id)),
      ];
    },
    setOrderPagination: (state, action: PayloadAction<OrderPagination>) => {
      state.pagination = action.payload;
    },
    setOrderSummary: (state, action: PayloadAction<OrderSummary>) => {
      state.summary = action.payload;
    },
    setOrderStatusFilter: (state, action: PayloadAction<string | undefined>) => {
      state.currentStatuses = parseStatusFilter(action.payload);
    },
    updateOrders: (state, action: PayloadAction<OrderFromDB>) => {
      const existingOrders = state.orders as OrderFromDB[];
      const nextOrder = {
        ...action.payload,
        status: normalizeOrderStatus(action.payload.status),
      };

      if (
        state.currentStatuses &&
        !state.currentStatuses.includes(nextOrder.status)
      ) {
        state.summary.activeCount += 1;
        return;
      }

      const orderUpdated = existingOrders.some(
        (order) =>
          order.id === nextOrder.id || order.orderNumber === nextOrder.orderNumber
      );

      if (orderUpdated) return;

      state.summary.activeCount += 1;
      state.orders = [nextOrder, ...existingOrders];
    },
    updateOrderStatus: (state, action: PayloadAction<OrderFromDB>) => {
      const updatedOrder = action.payload;
      const updatedOrders = state.orders
        .map((order) => (order.id === updatedOrder.id ? updatedOrder : order))
        .filter((order) => order.status !== 2 && order.status !== 3);

      return { ...state, orders: updatedOrders };
    },
  },
});

export const {
  setSocketConnect,
  setSocketDisconnect,
  setOrders,
  appendOrders,
  setOrderPagination,
  setOrderSummary,
  setOrderStatusFilter,
  updateOrders,
  updateOrderStatus,
} = OrderAndSocket.actions;

export default OrderAndSocket.reducer;
